import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ClipboardList, Activity, Users, Clock, Award, Building2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FixedWidgets from '../components/FixedWidgets';
import hospital from "../assets/building.png";
import drsantosh from "../assets/drsantosh.jpeg";
import isoCert from "../assets/iso.png";
import cert from "../assets/certificate.jpeg";

const HomePage = () => {

  const [selectedCert, setSelectedCert] = useState(null);

  const certifications = [
  {
    name: "ISO 9001:2015",
    image: isoCert,
    certi: cert
  },
];

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    initial: {},
    whileInView: { transition: { staggerChildren: 0.1 } },
    viewport: { once: true }
  };

  const services = [
    { 
      icon: ClipboardList, // Changed from Heart to better represent General Medicine
      title: 'General Medicine', 
      description: 'Comprehensive primary care, chronic disease management, and diagnostic expertise for adult health.' 
    },
    { icon: Activity, title: 'ICU & Critical Care', description: 'State-of-the-art intensive care units' },
    { icon: Users, title: 'Expert Consultants', description: 'Experienced medical consultants' },
    { icon: Clock, title: 'Emergency Services', description: 'Always ready to serve you 24/7' },
    { icon: Award, title: 'ISO Certified', description: 'Quality healthcare assured' },
    { icon: Building2, title: '25+ Bed Facility', description: 'Multispeciality hospital' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FixedWidgets />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 10, repeat: Infinity, repeatType: 'reverse' }}
          className="absolute inset-0 z-0"
        >
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(135deg, rgba(30, 58, 95, 0.95) 0%, rgba(45, 90, 123, 0.9) 50%, rgba(74, 144, 164, 0.85) 100%)'
            }}
          />
          <img
            src={hospital}
            alt="Hospital"
            className="w-full h-full object-cover"
          />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <motion.h1
              className="text-5xl md:text-7xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              Your Wellness
              <br />
              <span className="text-[#6FCF97]">Our Commitment</span>
            </motion.h1>

            <motion.p
              className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              25+ Bed Multispeciality Hospital & ISO Certified Hospital
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <a
                href="https://wa.me/917996524417?text=Hello%20I%20want%20to%20book%20an%20appointment"
                target="_blank"
                rel="noopener noreferrer"
              >
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: '0 10px 40px rgba(111, 207, 151, 0.3)' }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-[#6FCF97] text-white rounded-full font-semibold flex items-center space-x-2 hover:bg-[#5ABD82] transition-all duration-300"
                >
                  <span>Book Appointment</span>
                  <ArrowRight size={20} />
                </motion.button>
              </a>

              <Link to="/about">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-white/10 backdrop-blur-md text-white rounded-full font-semibold border-2 border-white/30 hover:bg-white/20 transition-all duration-300"
                >
                  Learn More
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-white rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-[#4A90A4]">Suvaidyam Hospital</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Committed to providing world-class healthcare with compassion and excellence
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -10, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 text-center flex flex-col items-center"

              >
                <motion.div
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  transition={{ duration: 0.5 }}
                  className="w-16 h-16 bg-gradient-to-br from-[#4A90A4] to-[#6FCF97] rounded-2xl flex items-center justify-center mb-6"
                >
                  <service.icon className="text-white" size={28} />
                </motion.div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="flex justify-center"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#4A90A4] to-[#6FCF97] rounded-3xl blur-2xl opacity-30" />
                <img
                    src={drsantosh}
                    alt="Dr. Santosh"
                    className="
                      relative z-10
                      w-[26rem]
                      sm:w-[30rem]
                      md:w-[39rem]
                      lg:w-[45rem]
                      h-auto
                      rounded-3xl
                      shadow-2xl
                      object-cover
                    "
                  />

              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                President & <span className="text-[#4A90A4]">CEO Message</span>
              </h2>

              <h3 className="text-2xl font-semibold text-gray-800 mb-2">
                DR. Santosh Vastrad
              </h3>

              <p className="text-[#6FCF97] font-semibold mb-6">
                Consultant Physician, Diabetes and Thyroid Specialist
              </p>

              <p className="text-gray-600 leading-relaxed mb-6">
                Our success is built on the tireless dedication of those who show up every day to make a difference. 
                To us, the 'white coat' isn't just a uniform—it is a symbol of our unwavering commitment to service. 
                We believe that by devoting ourselves to the well-being of others, we find our own greatest sense of purpose and fulfillment.
              </p>

              <p className="text-gray-600 leading-relaxed mb-6">
                <strong>Dr. Santosh Vastrad</strong> is an eminent <strong>Consultant Physician, Diabetes and Thyroid Specialist</strong> with 
                an illustrious career dedicated to excellence in internal medicine. With extensive experience 
                in managing complex metabolic disorders, he has successfully treated thousands of patients, specializing in : 
                <strong> Advanced Diabetes Management</strong>,<strong>Thyroid & Endocrine Disorders</strong>,<strong>Comprehensive Internal Medicine</strong> &  <strong>Preventative Care</strong>
              </p>

              

              {/* Qualifications */}
              <div className="mt-8 bg-gray-50 border border-gray-200 rounded-2xl p-6">
                <h4 className="text-xl font-bold text-gray-800 mb-4">
                  Education & Training
                </h4>   
                <ul className="space-y-2 text-gray-700 list-disc list-inside">
                  <li>MBBS – KMC Hubli (2003)</li>
                  <li>MD (Int. Med) (Department) – BLDE Medical College Vijapur (2008)</li>
                  <li>FDE (Endocrinology) – Manipal Hospital Bangalore (2010)</li>
                  
                </ul>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Accreditations */}
      <section className="py-24 bg-gradient-to-br from-[#1e3a5f] to-[#2d5a7b] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Accreditations & <span className="text-[#6FCF97]">Certifications</span>
            </h2>
            <p className="text-gray-300 text-lg">
              Recognized for excellence in healthcare delivery
            </p>
          </motion.div>

          {certifications.map((cert, index) => (
          <motion.div
            key={index}
            variants={fadeInUp}
            whileHover={{ scale: 1.05, y: -5 }}
            className="bg-white/10 backdrop-blur-md p-10 rounded-xl text-center border border-white/20 hover:bg-white/20 transition-all duration-300 w-full"
          >
            <img
              src={cert.certi}
              alt={cert.name}
              className="mx-auto mb-4 h-24 object-contain rounded-md"
            />

            <button
              onClick={() => setSelectedCert(cert.image)}
              className="mt-3 text-[#6FCF97] underline hover:text-white transition"
            >
              View Certificate
            </button>
          </motion.div>
        ))}



      <AnimatePresence>
              {selectedCert && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setSelectedCert(null)}
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
                >
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 120 }}
                    onClick={(e) => e.stopPropagation()}
                    className="relative w-full max-w-5xl h-[85vh] bg-white rounded-2xl shadow-2xl flex items-center justify-center p-6"
                  >
                    {/* Close Button */}
                    <button
                      onClick={() => setSelectedCert(null)}
                      className="absolute top-4 right-4 w-11 h-11 bg-gray-900 text-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition"
                    >
                      ✕
                    </button>

                    {/* Certificate Image */}
                    <img
                      src={selectedCert}
                      alt="Certificate"
                      className="max-w-full max-h-full object-contain rounded-lg"
                    />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>



              </div>
            </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <motion.div
          {...fadeInUp}
          className="max-w-5xl mx-auto px-6 text-center"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Ready to Experience
            <br />
            <span className="text-[#4A90A4]">World-Class Healthcare?</span>
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Our team of expert doctors and staff are here to provide you with the best medical care.
          </p>
          <Link to="/contact">
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: '0 10px 40px rgba(74, 144, 164, 0.3)' }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-5 bg-gradient-to-r from-[#4A90A4] to-[#6FCF97] text-white rounded-full font-bold text-lg flex items-center space-x-3 mx-auto hover:shadow-2xl transition-all duration-300"
            >
              <span>Contact Us Today</span>
              <ArrowRight size={24} />
            </motion.button>
          </Link>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;